<template>
  <div class="assign-location-field" :class="{ 'assign-location-field--inline': isInline }">
    <template v-if="isInline">
      <LinkWithIcon
        v-if="selectedLocation"
        class="assign-location-field__inline-location"
        isNoUnderline
        :isDisabled="props.isDisabled || isOpeningSelector"
        @click.stop="openLocationSelector"
      >
        <template #icon>
          <LoadingIcon v-if="isOpeningSelector" :size="16" />
          <img v-else-if="selectedLocationIconSrc" class="assign-location-field__inline-icon" :src="selectedLocationIconSrc" :alt="selectedLocation.name" @error="selectedLocationIconFailed = true" />
          <IconEmbedded v-else name="map-pin_2-5" :size="16" />
        </template>
        <span>{{ selectedLocation.name }}</span>
      </LinkWithIcon>
      <LinkWithIcon
        v-else
        class="assign-location-field__inline-set"
        isNoUnderline
        :isDisabled="props.isDisabled || isOpeningSelector"
        @click.stop="openLocationSelector"
      >
        <template #icon>
          <LoadingIcon v-if="isOpeningSelector" :size="16" />
          <IconEmbedded v-else name="map-pin_2-5" :size="16" />
        </template>
        <span>Set Business or Location</span>
      </LinkWithIcon>
    </template>
    <template v-else>
      <article v-if="selectedLocation" class="assign-location-field__snippet">
        <LocationAvatar :src="selectedLocation.icon" :name="selectedLocation.name" :size="44" />
        <div class="assign-location-field__copy">
          <strong>{{ selectedLocation.name }}</strong>
          <LocationSocialHandles :location="selectedLocation" />
          <small v-if="locationAddress">{{ locationAddress }}</small>
        </div>
        <button type="button" class="assign-location-field__action" :disabled="props.isDisabled || isOpeningSelector" @click="openLocationSelector">Change</button>
        <button type="button" class="assign-location-field__action assign-location-field__remove" :disabled="props.isDisabled" @click="clearLocation">Remove</button>
      </article>
      <SrpButton v-else fill="outlined" color="teal" :isDisabled="props.isDisabled || isOpeningSelector" @click="openLocationSelector">
        <template #icon>
          <LoadingIcon v-if="isOpeningSelector" :size="16" />
          <IconEmbedded v-else name="map-pin_2-5" :size="16" />
        </template>
        Set Business or Location
      </SrpButton>
    </template>

    <LocationPickerModal
      v-model:isVisible="isPickerVisible"
      :customerId="props.customerId"
      :reloadKey="pickerReloadKey"
      title="Set a business or location"
      createLabel="Add New Location"
      :allowedTagKinds="['Location']"
      @select="selectLocation"
      @create="openCreate"
      @edit="openEdit"
    />
    <LocationEditorModal v-model:isVisible="isEditorVisible" :customerId="props.customerId" :location="locationToEdit" @saved="handleLocationSaved" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LocationAvatar from "./LocationAvatar.vue";
import LocationSocialHandles from "./LocationSocialHandles.vue";
import LocationPickerModal from "./LocationPickerModal.vue";
import LocationEditorModal from "./LocationEditorModal.vue";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import type { LocationData, TaggableLocation } from "@contracts/socialLocations";

const props = withDefaults(
  defineProps<{
    customerId: string;
    locationId?: string | null;
    isDisabled?: boolean;
    autoOpen?: boolean;
    variant?: "full" | "inline";
  }>(),
  {
    locationId: null,
    isDisabled: false,
    autoOpen: false,
    variant: "full",
  }
);

const emit = defineEmits<{
  (e: "update:locationId", value: string | null): void;
}>();

const selectedLocation = ref<LocationData | null>(null);
const selectedLocationIconFailed = ref(false);
const isPickerVisible = ref(false);
const isEditorVisible = ref(false);
const isOpeningSelector = ref(false);
const pickerReloadKey = ref(0);
const locationToEdit = ref<LocationData | null>(null);
const openedEditorFromPicker = ref(false);
let resolveSequence = 0;

const isInline = computed(() => props.variant === "inline");

const locationAddress = computed(() => {
  const location = selectedLocation.value;
  if (!location) return "";
  return [location.address, [location.city, location.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
});

const selectedLocationIconSrc = computed(() => {
  if (selectedLocationIconFailed.value) return "";
  const value = selectedLocation.value?.icon?.trim();
  if (!value) return "";
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  const contentBaseUri = globalThis.Bootstrap?.Config?.contentCdnBaseUri ?? "";
  if (!contentBaseUri) return value;
  return `${contentBaseUri.replace(/\/$/, "")}/cms/images/expeditions/location-icons/${value.replace(/^\/+/, "")}`;
});

watch(
  () => selectedLocation.value?.icon,
  () => {
    selectedLocationIconFailed.value = false;
  }
);

async function resolveSelectedLocation(locationId: string | null) {
  const sequence = ++resolveSequence;
  if (!locationId) {
    selectedLocation.value = null;
    return;
  }
  if (selectedLocation.value?.id === locationId) return;
  const [location] = await SocialLocationsRepo.resolve(props.customerId, [locationId]);
  if (sequence !== resolveSequence) return;
  selectedLocation.value = location ?? null;
}

watch(
  () => [props.customerId, props.locationId] as const,
  ([, locationId]) => {
    void resolveSelectedLocation(locationId);
  },
  { immediate: true }
);

onMounted(() => {
  if (props.autoOpen && !props.locationId) void openLocationSelector();
});

function selectLocation(location: TaggableLocation) {
  emit("update:locationId", location.locationId);
}

function clearLocation() {
  emit("update:locationId", null);
}

function openCreate() {
  openedEditorFromPicker.value = true;
  locationToEdit.value = null;
  isEditorVisible.value = true;
}

async function openEdit(locationId: string) {
  openedEditorFromPicker.value = true;
  const [location] = await SocialLocationsRepo.resolve(props.customerId, [locationId]);
  locationToEdit.value = location ?? null;
  isEditorVisible.value = true;
}

async function openLocationSelector() {
  if (props.isDisabled || isOpeningSelector.value) return;
  isOpeningSelector.value = true;
  try {
    const locations = await SocialLocationsRepo.listTaggable(props.customerId);
    const hasAssignable = locations.some(location => (location.tagKind ?? "Location") === "Location");
    if (hasAssignable) isPickerVisible.value = true;
    else openCreate();
  } finally {
    isOpeningSelector.value = false;
  }
}

function handleLocationSaved(location: LocationData) {
  const isCreate = locationToEdit.value == null;
  const cameFromPicker = openedEditorFromPicker.value;
  openedEditorFromPicker.value = false;
  if (isCreate && location.tagKind !== "Person" && location.tagKind !== "Event") {
    emit("update:locationId", location.id);
    if (cameFromPicker) isPickerVisible.value = false;
    return;
  }
  if (selectedLocation.value?.id === location.id) selectedLocation.value = location;
  if (cameFromPicker && isPickerVisible.value) pickerReloadKey.value += 1;
}
</script>

<style scoped lang="scss">
.assign-location-field {
  display: grid;
  gap: 10px;

  &--inline {
    display: inline-flex;
    align-items: center;
    gap: 0;
  }
}

.assign-location-field__inline-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
}

.assign-location-field__snippet {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 14px;
  border: 1px solid #d7dce2;
  border-radius: 9px;
  background: #f9fbfb;
}

.assign-location-field__copy {
  flex: 1;
  display: grid;
  gap: 2px;
  min-width: 0;

  strong {
    font-size: 15px;
    color: #202733;
  }

  small {
    color: #667085;
    font-size: 13px;
  }
}

.assign-location-field__action {
  border: 0;
  background: transparent;
  color: gray;
  cursor: pointer;
}

.assign-location-field__remove {
  padding-left: 12px;
  border-left: 1px solid #d8dce2;
}
</style>
